// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — WS frame codec (parse / serialize / narrow) (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Pure frame shapes + narrowing helpers for the browser side of the loopback WS.
// The frame SHAPES mirror apps/node-companion/src/wsFrames.ts (HelloFrame /
// WelcomeFrameInput) + the on-wire frames the companion's wsServer.ts SENDS
// (`welcome` / `session-changed` / `rpc{method}` / `ping`) and RECEIVES
// (`hello` / `pong` / `rpc-result` / `rpc-error` / `document-changed` /
// `takeover` / `bye`).
//
// WHY MIRROR not IMPORT (ПРИОРИТЕТ #2): the companion frame schemas live ONLY in
// apps/node-companion (a sibling APP). A cross-app import (apps/web →
// apps/node-companion) is forbidden, and `contracts`/`ai-commands` do not carry
// these WS frame shapes (grepped 2026-06-22). So the shapes are re-declared here
// with this reference comment. The ONE value imported from a package is
// `AGENT_BRIDGE_PROTOCOL_VERSION` (the compiled contract version) from
// `@dxf-ai-editor/ai-commands/manifest` — the allowed dependency direction.
//
// LEAF module: imports ONLY `@dxf-ai-editor/ai-commands/manifest` (a type/value
// const). Never editorStore, never node-companion. Pure narrowing (no socket).
// ═══════════════════════════════════════════════════════════════════════════

import { AGENT_BRIDGE_PROTOCOL_VERSION } from "@dxf-ai-editor/ai-commands/manifest";

/** Re-export the compiled protocol version the client compares `welcome` against. */
export { AGENT_BRIDGE_PROTOCOL_VERSION };

/** The companion-served session-identity quad cached on the browser (welcome echo). */
export type SessionQuad = {
  sessionId: string;
  sessionEpoch: number;
  bootId: string;
  documentId: string | null;
  documentEpoch: number | null;
};

/** The seat role the welcome assigns (active = command executor; observer = read-only). */
export type BridgeRole = "active" | "observer";

// ── OUTBOUND frames (browser → companion) ───────────────────────────────────

/**
 * The pairing `hello` (§4.1; mirrors `HelloFrame` in node-companion/wsFrames.ts).
 * The token rides THIS FRAME, NEVER the URL/query (P2 token-isolation).
 */
export type OutboundHelloFrame = {
  type: "hello";
  token: string;
  clientInfo?: string;
  tabId?: string;
  documentId?: string;
  documentEpoch?: number;
};

/** The app-level `pong` reply to a companion `{type:ping,seq}` (echo seq verbatim). */
export type OutboundPongFrame = { type: "pong"; seq: number };

/** A terminal `rpc-result` for a forwarded rpc (echo correlationId + sessionEpoch). */
export type OutboundRpcResultFrame = {
  type: "rpc-result";
  correlationId: string;
  sessionEpoch: number;
  batchHash?: string;
  result?: unknown;
  outcomeId?: string;
};

/** A terminal `rpc-error` for a forwarded rpc (echo correlationId + sessionEpoch). */
export type OutboundRpcErrorFrame = {
  type: "rpc-error";
  correlationId: string;
  sessionEpoch: number;
  batchHash?: string;
  code: string;
  message: string;
  detail?: unknown;
  /**
   * Gate #4 / FIX#2 — a provisional outcome handle for the ERROR terminal,
   * SYMMETRIC with `OutboundRpcResultFrame.outcomeId`. The companion already
   * accepts it (`wsServer.ts` `onRpcReply` reads `frame.outcomeId` on BOTH
   * `rpc-result` and `rpc-error`, and `CommandTerminal`'s `{ok:false}` arm
   * carries `outcomeId?` — `correlation.ts`:152). The browser side lacked the
   * field, so it would SILENTLY DROP it the moment the cached-error-replay path
   * sets it. Optional (no producer sets it yet), but the plumbing must exist on
   * both halves so a future error outcomeId rides the wire. The companion
   * canonicalizes the durable id from its ledger, so the browser value is a
   * provisional echo (§3.6 / INV-13), never authoritative.
   */
  outcomeId?: string;
};

/**
 * The control frame announcing a true document SWAP (browser → companion). The
 * `documentEpoch` is OMITTED when null (mirroring the hello, `buildHelloFrame`): the
 * companion's `validateOptionalDocumentEpoch` reads an ABSENT field as the
 * document-less `null` default, whereas an explicit `0` would set epoch 0 — so a
 * truly epoch-less document must omit the field to round-trip faithfully (not coerce
 * to 0).
 */
export type OutboundDocumentChangedFrame = {
  type: "document-changed";
  documentId: string | null;
  documentEpoch?: number;
};

/**
 * ABB-S5 — a browser STATE-CHANGE push (browser → companion). The companion
 * stages the `payload` OPAQUELY into the per-session journal + fans it out on the
 * SSE stream (`brokerReadFace.ts onBrowserPush` reads ONLY `event` +
 * `sessionEpoch` + `payload`; it stages the payload without inspecting it,
 * INV-1). The browser ALSO stamps `channel:'agent-bridge'` +
 * `actorEmail:'agent-bridge@local.invalid'` at the FRAME level (SM-5) — the
 * companion ignores the extra fields, but they document the originating
 * channel/actor on the wire (and mirror the LOCAL ring tagging ABB-S5a applies
 * via the ambient bridge-turn marker). ONLY two `event` values are emitted by the
 * browser: `audit` (the staged-until-commit turn audit, SM-5) and `selection`
 * (the passive selection diff, SM-6). The browser NEVER emits a `command-result`
 * push (SM-7 — the companion auto-emits the canonical command-result from the
 * rpc-result terminal with its OWN authoritative outcomeId; a browser one would
 * land a SECOND journal entry with a DIFFERENT outcomeId, defeating the agent's
 * outcomeId de-dup).
 */
export type OutboundPushFrame = {
  type: "push";
  event: "audit" | "selection";
  sessionEpoch: number;
  payload: unknown;
  channel: "agent-bridge";
  actorEmail: string;
};

/**
 * A terminal `export-error` for an `export.dxf` rpc (browser → companion). The
 * companion routes export terminals through `ExportPump.onExportFrame`
 * (`exportBroker.ts`:225-254), matched by `correlationId === pending.exportId` AND
 * `sessionEpoch === pending.sessionEpoch` — an `rpc-error` carrying an `export-…`
 * correlationId is NOT matched there (it falls to `onRpcReply`, which finds no
 * command slot → drop), so the HTTP export HANGS until the stall/overall deadline.
 * An unwired/failed export.dxf therefore MUST reply on the EXPORT wire so the
 * companion fails it IMMEDIATELY. The pump reads `code`/`message` (code defaults to
 * `browser-dispatch-error` if absent). Mirror of `exportBroker.ts`:249-253,
 * captured 2026-06-22.
 */
export type OutboundExportErrorFrame = {
  type: "export-error";
  correlationId: string;
  sessionEpoch: number;
  code: string;
  message: string;
};

/**
 * A terminal `export-complete` for an `export.dxf` rpc (browser → companion). The
 * companion's `ExportPump.onComplete` (`exportBroker.ts`:349-385) matches it by
 * `correlationId === pending.exportId` AND `sessionEpoch === pending.sessionEpoch`,
 * then VERIFIES `byteLength` is a non-negative integer that EQUALS the sum of the
 * DECODED `export-chunk` bytes it streamed (a mismatch / non-integer aborts WITHOUT
 * the success trailer). `snapshotRevision` is surfaced to the route once before the
 * first byte (`onStart`). A SUCCESSFUL `export.dxf` outcome therefore MUST reply on
 * the EXPORT wire (this frame) — an `rpc-result` carrying an `export-…` correlationId
 * is NOT matched by the pump (it falls to `onRpcReply` → no command slot → drop) and
 * the HTTP export HANGS until the stall/overall deadline. Mirror of `exportBroker.ts`
 * :349-385, captured 2026-06-22.
 */
export type OutboundExportCompleteFrame = {
  type: "export-complete";
  correlationId: string;
  sessionEpoch: number;
  byteLength: number;
  snapshotRevision?: number | null;
};

/**
 * ABB-S6 — a streamed `export-chunk` for an `export.dxf` rpc (browser → companion).
 * The companion's `ExportPump.onExportFrame` matches it by `correlationId ===
 * pending.exportId` AND `sessionEpoch === pending.sessionEpoch` (the export's ORIGIN
 * epoch — NOT the current welcome epoch; the producer stamps the ORIGIN epoch so a
 * soft-rekey mid-export does not epoch-drop the chunk — `exportBroker.ts`:239-244),
 * then `decodeChunk` STRICT-validates `chunk` is CANONICAL STANDARD base64 (`+`/`/`,
 * NOT base64url) and aborts no-trailer on any non-canonical char (`exportBroker.ts`
 * :411-423/465-482). `snapshotRevision` is surfaced to the route ONCE before the first
 * byte (`onStart`, [H2]); it is OMITTED when undefined and an explicit `null` is
 * preserved (a document with no revision counter — the companion coerces a non-integer
 * to `null`, `coerceSnapshotRevision`). Mirror of `exportBroker.ts`:309-348, captured
 * 2026-06-23.
 */
export type OutboundExportChunkFrame = {
  type: "export-chunk";
  correlationId: string;
  sessionEpoch: number;
  /** The opaque DXF bytes as CANONICAL STANDARD base64 (INV-1 — never parsed by the companion). */
  chunk: string;
  /** Pinned at stream start; surfaced to the route's `onStart` before the first byte ([H2]). */
  snapshotRevision?: number | null;
};

export type OutboundFrame =
  | OutboundHelloFrame
  | OutboundPongFrame
  | OutboundRpcResultFrame
  | OutboundRpcErrorFrame
  | OutboundDocumentChangedFrame
  | OutboundPushFrame
  | OutboundExportChunkFrame
  | OutboundExportErrorFrame
  | OutboundExportCompleteFrame;

// ── INBOUND frames (companion → browser) ────────────────────────────────────

/** The pairing `welcome` (§4.1; mirrors the companion `buildWelcomeFrame` output). */
export type InboundWelcomeFrame = {
  type: "welcome";
  role: BridgeRole;
  sessionId: string;
  sessionEpoch: number;
  bootId: string;
  documentId: string | null;
  documentEpoch: number | null;
  protocolVersion: string;
};

/** Broadcast when the active seat / epoch changed (wsServer `broadcastSessionChanged`). */
export type InboundSessionChangedFrame = {
  type: "session-changed";
  sessionId: string;
  sessionEpoch: number;
};

/** A companion-forwarded rpc (ALWAYS companion → browser — the wire is asymmetric). */
export type InboundRpcFrame = {
  type: "rpc";
  method: string;
  correlationId: string;
  sessionEpoch: number;
  deadlineBudgetMs?: number;
  payload?: unknown;
};

/** The app-level heartbeat ping the page JS must answer with a `pong` (NOT auto-pong). */
export type InboundPingFrame = { type: "ping"; seq: number };

/**
 * The export FLOW-CONTROL frames the companion's `ExportPump` sends to the PRODUCING
 * tab (`exportBroker.ts` `export-pause`/`export-resume`/`export-cancel`). They carry
 * the export's `correlationId` (the companion-minted `export-…` id) + the export's
 * `sessionEpoch` (§5.0 epoch-drop). PARSED + ROUTED to the DI export-control seam
 * (inert until ABB-S6 wires the producer) — NEVER silently dropped: once export.dxf
 * is wired the producer MUST honor a pause/cancel or it streams unboundedly past the
 * companion's credit cap (`exportBroker.ts` [H10]). Mirror of `exportBroker.ts`:269
 * (resume), :345 (pause), :290 (cancel), captured 2026-06-22.
 */
export type InboundExportControlFrame = {
  type: "export-pause" | "export-resume" | "export-cancel";
  correlationId: string;
  sessionEpoch: number;
};

/**
 * A companion takeover control frame (browser does not act on this in v1). The
 * companion legitimately emits `takeover{ok:false}` (a denied seat request reply), so
 * `takeover` IS a companion→browser inbound frame. There is NO inbound `bye`: `bye` is
 * browser→companion ONLY (the companion RECEIVES it + closes GOING_AWAY at
 * `wsServer.ts`:864-866 — it never SENDS `bye`), so a spurious inbound `bye` is dropped
 * by the codec (fail-closed), not mirrored as a phantom inbound frame. [round-3 P3 #5]
 */
export type InboundTakeoverFrame = { type: "takeover"; [k: string]: unknown };

export type InboundFrame =
  | InboundWelcomeFrame
  | InboundSessionChangedFrame
  | InboundRpcFrame
  | InboundPingFrame
  | InboundExportControlFrame
  | InboundTakeoverFrame;

/** The set of rpc methods the dispatch router recognizes (handlers wired by S4/S6). */
export const KNOWN_RPC_METHODS = [
  "command.run",
  "state.read",
  "export.dxf",
  "manifest.read",
] as const;

export type KnownRpcMethod = (typeof KNOWN_RPC_METHODS)[number];

/** Whether `method` is a recognized rpc method (vs a genuinely unknown one). */
export function isKnownRpcMethod(method: string): method is KnownRpcMethod {
  return (KNOWN_RPC_METHODS as readonly string[]).includes(method);
}

// ── outbound frame builders (pure — keep the client thin) ───────────────────

/** Build the pairing `hello` (token in the FRAME, never the URL; absent fields omitted). */
export function buildHelloFrame(input: {
  token: string;
  clientInfo?: string;
  tabId?: string;
  documentId: string | null;
  documentEpoch: number | null;
}): OutboundHelloFrame {
  return {
    type: "hello",
    token: input.token,
    ...(input.clientInfo !== undefined ? { clientInfo: input.clientInfo } : {}),
    ...(input.tabId !== undefined ? { tabId: input.tabId } : {}),
    ...(input.documentId !== null ? { documentId: input.documentId } : {}),
    ...(input.documentEpoch !== null ? { documentEpoch: input.documentEpoch } : {}),
  };
}

/**
 * Gate #4 — the SHARED envelope keys every rpc TERMINAL frame (result AND error)
 * carries. Anchored as a typed constant so the paired-symmetry gate
 * (`pairedSymmetryGate.test.ts`) can assert the result frame and the error frame
 * expose the IDENTICAL shared-envelope key set — a future field added to one
 * builder but not the other turns the gate RED. `correlationId` + `sessionEpoch`
 * are always present; `batchHash` + `outcomeId` are optional (omitted when
 * absent) but BOTH builders must be ABLE to carry them (the symmetry the gate
 * pins). NOTE: `type` is the discriminant (intentionally per-frame, not shared);
 * `result`/`code`/`message`/`detail` are arm-specific payload, not envelope.
 */
export const RPC_TERMINAL_ENVELOPE_KEYS = [
  "correlationId",
  "sessionEpoch",
  "batchHash",
  "outcomeId",
] as const;

export type RpcTerminalEnvelopeKey = (typeof RPC_TERMINAL_ENVELOPE_KEYS)[number];

/** The shared rpc-terminal envelope inputs (the keys in `RPC_TERMINAL_ENVELOPE_KEYS`). */
type RpcEnvelopeInput = {
  correlationId: string;
  sessionEpoch: number;
  batchHash?: string;
  outcomeId?: string;
};

/**
 * Gate #4 — derive the SHARED envelope of BOTH terminal frames from one place so
 * the result builder and the error builder can never diverge on an envelope key:
 * `correlationId` + `sessionEpoch` always emitted; `batchHash` + `outcomeId`
 * spread only when defined (no `key: undefined` on the wire). The paired-symmetry
 * gate enumerates `RPC_TERMINAL_ENVELOPE_KEYS` against this output.
 */
function buildRpcEnvelopeBase(
  input: RpcEnvelopeInput,
): Pick<OutboundRpcResultFrame, RpcTerminalEnvelopeKey> {
  return {
    correlationId: input.correlationId,
    sessionEpoch: input.sessionEpoch,
    ...(input.batchHash !== undefined ? { batchHash: input.batchHash } : {}),
    ...(input.outcomeId !== undefined ? { outcomeId: input.outcomeId } : {}),
  };
}

/** Build a terminal `rpc-result` (echo correlationId + sessionEpoch; absent fields omitted). */
export function buildRpcResultFrame(input: {
  correlationId: string;
  sessionEpoch: number;
  batchHash?: string;
  result?: unknown;
  outcomeId?: string;
}): OutboundRpcResultFrame {
  return {
    type: "rpc-result",
    ...buildRpcEnvelopeBase(input),
    result: input.result,
  };
}

/** Build a terminal `rpc-error` (echo correlationId + sessionEpoch; absent fields omitted). */
export function buildRpcErrorFrame(input: {
  correlationId: string;
  sessionEpoch: number;
  batchHash?: string;
  code: string;
  message: string;
  detail?: unknown;
  outcomeId?: string;
}): OutboundRpcErrorFrame {
  return {
    type: "rpc-error",
    ...buildRpcEnvelopeBase(input),
    code: input.code,
    message: input.message,
    ...(input.detail !== undefined ? { detail: input.detail } : {}),
  };
}

/**
 * Build a `document-changed` control frame (browser → companion). OMITS
 * `documentEpoch` when null so the companion reads it as the document-less default
 * (an explicit `0` would set epoch 0 — `validateOptionalDocumentEpoch`); mirrors the
 * hello's documentEpoch omission for a faithful round-trip.
 */
export function buildDocumentChangedFrame(input: {
  documentId: string | null;
  documentEpoch: number | null;
}): OutboundDocumentChangedFrame {
  return {
    type: "document-changed",
    documentId: input.documentId,
    ...(input.documentEpoch !== null ? { documentEpoch: input.documentEpoch } : {}),
  };
}

/**
 * ABB-S5 — build a browser STATE-CHANGE `push` frame (browser → companion). The
 * `payload` is carried OPAQUELY (INV-1 — the companion stages it without
 * inspecting it). The `channel`/`actorEmail` are stamped at the frame level (SM-5)
 * to document the originating channel/synthetic actor on the wire. Used by ABB-S5
 * for the `audit` (staged-until-commit) + `selection` (passive diff) pushes ONLY —
 * the browser NEVER emits a `command-result` push (SM-7).
 */
export function buildPushFrame(input: {
  event: "audit" | "selection";
  sessionEpoch: number;
  payload: unknown;
  actorEmail: string;
}): OutboundPushFrame {
  return {
    type: "push",
    event: input.event,
    sessionEpoch: input.sessionEpoch,
    payload: input.payload,
    channel: "agent-bridge",
    actorEmail: input.actorEmail,
  };
}

/**
 * Build a terminal `export-error` (browser → companion) for an unwired/failed
 * `export.dxf` rpc. Echoes the export `correlationId` + `sessionEpoch` so the
 * companion's `ExportPump` matches it to the in-flight export (`exportBroker.ts`
 * :239-253) and aborts IMMEDIATELY — never the stall/overall-deadline wait an
 * mis-routed `rpc-error` would incur.
 */
export function buildExportErrorFrame(input: {
  correlationId: string;
  sessionEpoch: number;
  code: string;
  message: string;
}): OutboundExportErrorFrame {
  return {
    type: "export-error",
    correlationId: input.correlationId,
    sessionEpoch: input.sessionEpoch,
    code: input.code,
    message: input.message,
  };
}

/**
 * Build a terminal `export-complete` (browser → companion) for a SUCCESSFUL
 * `export.dxf` rpc. Echoes the export `correlationId` + `sessionEpoch` so the
 * companion's `ExportPump.onComplete` matches it to the in-flight export
 * (`exportBroker.ts`:349-385) and seals the HTTP body with the success trailer.
 * `byteLength` is the sum of the DECODED chunk bytes (the companion ABORTS on a
 * mismatch). `snapshotRevision` is OMITTED when undefined; an explicit `null` is
 * preserved (the producer pins a document with no revision counter — the companion
 * reads a non-number as `null`).
 */
export function buildExportCompleteFrame(input: {
  correlationId: string;
  sessionEpoch: number;
  byteLength: number;
  snapshotRevision?: number | null;
}): OutboundExportCompleteFrame {
  return {
    type: "export-complete",
    correlationId: input.correlationId,
    sessionEpoch: input.sessionEpoch,
    byteLength: input.byteLength,
    ...(input.snapshotRevision !== undefined
      ? { snapshotRevision: input.snapshotRevision }
      : {}),
  };
}

/**
 * ABB-S6 — build a streamed `export-chunk` (browser → companion). `chunk` MUST be
 * CANONICAL STANDARD base64 (the producer encodes the immutable captured DXF slice —
 * `exportDxfHandler.ts`); a non-canonical char makes the companion abort no-trailer
 * (`exportBroker.ts`:419). `snapshotRevision` is OMITTED when undefined so the companion
 * surfaces `null` (a document with no revision counter); an explicit `null` is preserved.
 * Echoes the export `correlationId` + its ORIGIN `sessionEpoch` so the pump matches it.
 */
export function buildExportChunkFrame(input: {
  correlationId: string;
  sessionEpoch: number;
  chunk: string;
  snapshotRevision?: number | null;
}): OutboundExportChunkFrame {
  return {
    type: "export-chunk",
    correlationId: input.correlationId,
    sessionEpoch: input.sessionEpoch,
    chunk: input.chunk,
    ...(input.snapshotRevision !== undefined
      ? { snapshotRevision: input.snapshotRevision }
      : {}),
  };
}

// ── serialize ───────────────────────────────────────────────────────────────

/** Serialize an outbound frame to the JSON text the WS `send` carries. */
export function serializeFrame(frame: OutboundFrame): string {
  return JSON.stringify(frame);
}

// ── parse + narrow (defensive — the wire is untrusted) ──────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Parse raw WS message data (string or ArrayBuffer/Blob-decoded string) into a
 * narrowed inbound frame, or `null` if it is not a well-formed known inbound
 * frame. Never throws (a malformed wire payload is dropped, fail-closed).
 */
export function parseInboundFrame(raw: unknown): InboundFrame | null {
  if (typeof raw !== "string") {
    return null; // the bridge speaks JSON text frames only.
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const frame = asRecord(parsed);
  if (frame === null || typeof frame.type !== "string") {
    return null;
  }
  switch (frame.type) {
    case "welcome":
      return narrowWelcome(frame);
    case "session-changed":
      return narrowSessionChanged(frame);
    case "rpc":
      return narrowRpc(frame);
    case "ping":
      return narrowPing(frame);
    case "export-pause":
    case "export-resume":
    case "export-cancel":
      return narrowExportControl(frame.type, frame);
    case "takeover":
      return { type: "takeover", ...frame } as InboundTakeoverFrame;
    // [round-3 P3 #5] NO `case "bye"`: `bye` is browser→companion only; an inbound `bye`
    // is direction-inverted noise → falls through to `default` and is dropped (fail-closed).
    default:
      return null; // unknown frame type → dropped.
  }
}

function narrowWelcome(frame: Record<string, unknown>): InboundWelcomeFrame | null {
  if (frame.role !== "active" && frame.role !== "observer") {
    return null;
  }
  if (
    typeof frame.sessionId !== "string" ||
    typeof frame.sessionEpoch !== "number" ||
    typeof frame.bootId !== "string" ||
    typeof frame.protocolVersion !== "string"
  ) {
    return null;
  }
  const documentId =
    frame.documentId === null || typeof frame.documentId === "string"
      ? (frame.documentId as string | null)
      : null;
  const documentEpoch =
    frame.documentEpoch === null || typeof frame.documentEpoch === "number"
      ? (frame.documentEpoch as number | null)
      : null;
  return {
    type: "welcome",
    role: frame.role,
    sessionId: frame.sessionId,
    sessionEpoch: frame.sessionEpoch,
    bootId: frame.bootId,
    documentId,
    documentEpoch,
    protocolVersion: frame.protocolVersion,
  };
}

function narrowSessionChanged(
  frame: Record<string, unknown>,
): InboundSessionChangedFrame | null {
  if (typeof frame.sessionId !== "string" || typeof frame.sessionEpoch !== "number") {
    return null;
  }
  return {
    type: "session-changed",
    sessionId: frame.sessionId,
    sessionEpoch: frame.sessionEpoch,
  };
}

function narrowRpc(frame: Record<string, unknown>): InboundRpcFrame | null {
  if (
    typeof frame.method !== "string" ||
    typeof frame.correlationId !== "string" ||
    typeof frame.sessionEpoch !== "number"
  ) {
    return null;
  }
  const deadlineBudgetMs =
    typeof frame.deadlineBudgetMs === "number" ? frame.deadlineBudgetMs : undefined;
  return {
    type: "rpc",
    method: frame.method,
    correlationId: frame.correlationId,
    sessionEpoch: frame.sessionEpoch,
    deadlineBudgetMs,
    payload: frame.payload,
  };
}

function narrowPing(frame: Record<string, unknown>): InboundPingFrame | null {
  if (typeof frame.seq !== "number") {
    return null;
  }
  return { type: "ping", seq: frame.seq };
}

function narrowExportControl(
  type: "export-pause" | "export-resume" | "export-cancel",
  frame: Record<string, unknown>,
): InboundExportControlFrame | null {
  if (typeof frame.correlationId !== "string" || typeof frame.sessionEpoch !== "number") {
    return null; // a malformed export-control frame → dropped (fail-closed).
  }
  return { type, correlationId: frame.correlationId, sessionEpoch: frame.sessionEpoch };
}
